<template>
    <form class="form-signin" @submit.prevent="verify" data-test-id="form-mfa-challenge">
        <div class="login-card">
            <header class="login-header">
                <img class="login-logo" width="200" src="/images/logo-lead.png" :alt="themeBrandName" />
                <div class="login-heading-row">
                    <div>
                        <h5 class="login-title" data-test-id="title-mfa-challenge">{{ $tl("labels.form.mfa.title") }}</h5>
                    </div>
                    <ChooseLang class="login-lang" />
                </div>
            </header>

            <LoaderComponent v-if="loading" class="login-loader" />

            <template v-else>
                <div v-if="errorMessage" id="mfa-error" class="alert alert-danger login-alert" role="alert" aria-live="polite">
                    {{ errorMessage }}
                </div>

                <template v-if="recoveryUrl === null && !statusLoadFailed">
                    <p class="text-muted mb-4">
                        {{ $tl("labels.form.mfa.instructions", undefined, { email: maskedEmail }) }}
                    </p>

                    <div v-if="isPending" class="alert alert-info login-alert" role="status" aria-live="polite">
                        {{ $tl("labels.form.mfa.sending") }}
                    </div>

                    <div class="login-fields">
                        <div class="login-field">
                            <label for="mfa-code" class="form-label login-label">{{ $tl("labels.form.mfa.code") }}</label>
                            <input
                                id="mfa-code"
                                ref="codeInput"
                                v-model="code"
                                data-test-id="input-mfa-code"
                                type="text"
                                class="form-control mfa-code-input"
                                :class="{ 'is-invalid': isCodeError }"
                                :aria-invalid="isCodeError ? 'true' : 'false'"
                                :aria-describedby="isCodeError ? 'mfa-error' : undefined"
                                inputmode="numeric"
                                autocomplete="one-time-code"
                                maxlength="6"
                                pattern="[0-9]{6}"
                                required
                                autofocus
                                @input="normalizeCode" />
                        </div>
                    </div>

                    <label v-if="trustedDeviceDays > 0" class="form-label login-checkbox mfa-trusted-device" for="trust-device">
                        <input id="trust-device" v-model="trustDevice" data-test-id="input-trust-device" type="checkbox" />
                        <span class="checkmark"></span>
                        <span>{{ $tl("labels.form.mfa.trustDevice", undefined, { days: trustedDeviceDays }) }}</span>
                    </label>

                    <button
                        data-test-id="btn-verify-mfa"
                        type="submit"
                        class="btn btn-success btn-circle login-submit"
                        :disabled="submitting || isPending || code.length !== 6">
                        {{ $tl("labels.form.mfa.verify") }}
                    </button>

                    <div class="login-register-link mfa-resend">
                        <span v-if="resendRemaining > 0">
                            {{ $tl("labels.form.mfa.resendCountdown", undefined, { seconds: resendRemaining }) }}
                        </span>
                        <button v-else data-test-id="btn-resend-mfa" type="button" class="btn btn-link p-0" :disabled="resending" @click="resend">
                            {{ $tl("labels.form.mfa.resend") }}
                        </button>
                    </div>
                </template>

                <button
                    v-else-if="statusLoadFailed"
                    ref="retryStatusButton"
                    data-test-id="btn-mfa-retry-status"
                    type="button"
                    class="btn btn-success btn-circle login-submit"
                    @click="retryStatus">
                    {{ $tl("labels.form.mfa.retry") }}
                </button>

                <button
                    v-else
                    ref="recoveryButton"
                    data-test-id="btn-mfa-recovery"
                    type="button"
                    class="btn btn-success btn-circle login-submit"
                    @click="recover">
                    {{ $tl("labels.form.mfa.backToCustomers") }}
                </button>

                <div class="login-register-link mfa-account-switch">
                    <button data-test-id="btn-mfa-logout" type="button" class="btn btn-link p-0" :disabled="loggingOut" @click="logout">
                        {{ $tl("labels.form.mfa.useAnotherAccount") }}
                    </button>
                </div>
            </template>
        </div>
    </form>
</template>

<script>
import axios from "axios";
import { mfaVerificationFailure, mfaVerificationRedirect } from "../../auth/mfaVerificationResult.mjs";
import ChooseLang from "../../components/Default/ChooseLang.vue";
import LoaderComponent from "../../components/LoaderComponent.vue";

export default {
    name: "MfaChallenge",
    components: { ChooseLang, LoaderComponent },
    data() {
        return {
            loading: true,
            submitting: false,
            resending: false,
            loggingOut: false,
            code: "",
            errorMessage: null,
            errorReason: null,
            recoveryUrl: null,
            statusLoadFailed: false,
            challengeReady: false,
            status: "pending",
            maskedEmail: "",
            resendRemaining: 0,
            trustedDeviceDays: 0,
            trustDevice: false,
            timer: null,
            refreshTicks: 0,
        };
    },
    computed: {
        isPending() {
            return ["pending", "sending"].includes(this.status);
        },
        isCodeError() {
            return mfaVerificationFailure({ data: { reason: this.errorReason } }).isCodeError;
        },
    },
    mounted() {
        this.loadStatus().finally(() => {
            this.loading = false;
            this.focusStatusRetryIfNeeded();
        });

        this.timer = window.setInterval(() => {
            if (this.resendRemaining > 0) {
                this.resendRemaining--;
            }

            this.refreshTicks++;
            if (this.isPending && this.refreshTicks % 3 === 0) {
                this.loadStatus();
            }
        }, 1000);
    },
    beforeUnmount() {
        if (this.timer !== null) {
            window.clearInterval(this.timer);
        }
    },
    methods: {
        normalizeCode() {
            this.code = this.code.replace(/\D/g, "").slice(0, 6);
            this.errorMessage = null;
            this.errorReason = null;
        },
        loadStatus() {
            return axios
                .get("/api/mfa/challenge")
                .then((response) => {
                    this.errorMessage = null;
                    this.errorReason = null;
                    this.recoveryUrl = null;
                    this.statusLoadFailed = false;

                    if (!response.data.required) {
                        window.location.href = response.data.redirect_url || "/";
                        return;
                    }

                    this.status = response.data.status;
                    this.challengeReady = true;
                    this.maskedEmail = response.data.masked_email;
                    this.resendRemaining = response.data.resend_available_in_seconds || 0;
                    this.trustedDeviceDays = response.data.trusted_device_days || 0;
                })
                .catch((error) => {
                    this.errorMessage = this.responseMessage(error);
                    this.statusLoadFailed = !this.challengeReady;
                });
        },
        retryStatus() {
            this.loading = true;

            this.loadStatus().finally(() => {
                this.loading = false;
                this.focusStatusRetryIfNeeded();
            });
        },
        focusStatusRetryIfNeeded() {
            if (!this.statusLoadFailed) {
                return;
            }

            this.$nextTick(() => {
                this.$refs.retryStatusButton?.focus();
            });
        },
        verify() {
            this.submitting = true;
            this.errorMessage = null;

            axios
                .post("/api/mfa/challenge/verify", {
                    code: this.code,
                    trust_device: this.trustDevice,
                })
                .then((response) => {
                    const redirectUrl = mfaVerificationRedirect(response.data);

                    if (redirectUrl === null) {
                        this.errorMessage = response.data.message || this.$tl("labels.form.mfa.genericError");
                        return;
                    }

                    window.location.href = redirectUrl;
                })
                .catch((error) => {
                    const failure = mfaVerificationFailure(error.response);

                    this.errorMessage = this.responseMessage(error);
                    this.errorReason = failure.reason;
                    this.recoveryUrl = failure.recoveryUrl;

                    if (this.recoveryUrl === null) {
                        this.code = "";
                        this.$nextTick(() => {
                            this.$refs.codeInput?.focus();
                        });
                    } else {
                        this.$nextTick(() => {
                            this.$refs.recoveryButton?.focus();
                        });
                    }

                    if (["code_expired", "code_locked"].includes(error.response?.data?.reason)) {
                        this.status = error.response.data.reason === "code_expired" ? "expired" : "locked";
                    }
                })
                .finally(() => {
                    this.submitting = false;
                });
        },
        logout() {
            this.loggingOut = true;
            this.errorMessage = null;

            axios
                .post("/logout")
                .then((response) => {
                    window.location.replace(response.data.redirect_url || response.headers.location || "/login");
                })
                .catch((error) => {
                    this.errorMessage = this.responseMessage(error);
                    this.$nextTick(() => {
                        this.$refs.codeInput?.focus();
                    });
                })
                .finally(() => {
                    this.loggingOut = false;
                });
        },
        recover() {
            window.location.href = this.recoveryUrl || "/admin/owners";
        },
        resend() {
            this.resending = true;
            this.errorMessage = null;
            this.errorReason = null;

            axios
                .post("/api/mfa/challenge/resend")
                .then((response) => {
                    this.status = "pending";
                    this.code = "";
                    this.resendRemaining = response.data.resend_available_in_seconds || 0;
                    this.refreshTicks = 0;
                })
                .catch((error) => {
                    this.errorMessage = this.responseMessage(error);
                    const retryAfter = Number(error.response?.headers?.["retry-after"] || 0);
                    this.resendRemaining = Math.max(this.resendRemaining, retryAfter);
                })
                .finally(() => {
                    this.resending = false;
                });
        },
        responseMessage(error) {
            return error.response?.data?.message || this.$tl("labels.form.mfa.genericError");
        },
    },
};
</script>
